import { useState, useEffect, useCallback } from 'react';
import { Loader2, FileText, Eye, ChevronLeft, ChevronRight, AlertCircle, AlertTriangle, ChevronDown, ChevronUp, Lock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { invoicingAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmtAmount(v, currency) {
  if (v == null || v === '') return '-';
  const sym = currency === 'EUR' ? '\u20ac' : currency || '';
  return `${sym}${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function statusBadge(inv) {
  if (inv.draft) return <Badge variant="outline" className="text-amber-600 border-amber-400">Draft</Badge>;
  // FA matched the money in a bank account (often an owner's account Pennylane
  // cannot see), so it is received even if Pennylane still says late.
  if (inv.fa_matching_status === 'matched')
    return (
      <Badge variant="outline" className="text-green-700 border-green-500"
        title={`Money received (matched in Finance Agent). Pennylane says: ${inv.pennylane_status || 'unknown'}`}>
        Received
      </Badge>
    );
  if (inv.fa_matching_status === 'partially_matched')
    return (
      <Badge variant="outline" className="text-amber-700 border-amber-500"
        title={`Part of the money received. Pennylane says: ${inv.pennylane_status || 'unknown'}`}>
        Part received
      </Badge>
    );
  if (inv.status === 'late')
    return <Badge variant="outline" className="text-red-600 border-red-400">Not received, overdue</Badge>;
  if (inv.status === 'finalized' || inv.status === 'accepted')
    return <Badge variant="outline" className="text-green-600 border-green-400">Finalised</Badge>;
  return <Badge variant="outline">{inv.status || 'unknown'}</Badge>;
}

function computeLineTotal(line) {
  const qty = Number(line.quantity) || 0;
  const unitPrice = Number(line.raw_currency_unit_price ?? line.unit_price) || 0;
  const amount = Number(line.currency_amount ?? line.amount) || 0;
  if (unitPrice !== 0) return qty * unitPrice;
  return amount;
}

function ReconciliationRow({ lines, invoiceAmount, currency }) {
  if (!lines?.length) return null;

  const lineSum = lines.reduce((acc, line) => acc + computeLineTotal(line), 0);
  const total = Number(invoiceAmount) || 0;
  const diff = Math.abs(lineSum - total);
  const mismatch = diff > 0.01;
  const hasCommission = lines.some(
    (l) => (l.label || '').toLowerCase().includes('commission') && Number(l.quantity) < 0
  );

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs font-medium pt-1 border-t">
        <span>Sum of lines</span>
        <span className="tabular-nums">{fmtAmount(lineSum, currency)}</span>
      </div>
      <div className="flex justify-between text-xs">
        <span>Pennylane total</span>
        <span className="tabular-nums">{fmtAmount(total, currency)}</span>
      </div>
      {mismatch && (
        <div className="flex items-center gap-1 text-xs text-amber-700 bg-amber-50 dark:text-amber-300 dark:bg-amber-950/50 rounded px-2 py-1">
          <AlertTriangle className="h-3 w-3 flex-shrink-0" />
          Line sum ({fmtAmount(lineSum, currency)}) does not match Pennylane total ({fmtAmount(total, currency)}).
          Difference: {fmtAmount(diff, currency)}.
        </div>
      )}
      {!hasCommission && total > 0 && (
        <div className="flex items-center gap-1 text-xs text-amber-700 bg-amber-50 dark:text-amber-300 dark:bg-amber-950/50 rounded px-2 py-1">
          <AlertTriangle className="h-3 w-3 flex-shrink-0" />
          No commission deduction line. Total may be gross (not net revenue).
        </div>
      )}
    </div>
  );
}

// ── Invoice detail modal (PDF or line-item fallback) ─────────────────

function InvoiceDetailModal({ entityId, invoice, onClose, onRequestFinalize }) {
  const [phase, setPhase] = useState('loading'); // loading | pdf | detail | error
  const [pdfUrl, setPdfUrl] = useState(null);
  const [detail, setDetail] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    if (!invoice) return;
    let cancelled = false;

    (async () => {
      try {
        // Fetch detail first to get line items (used as fallback)
        const { data: detailData } = await invoicingAPI.viewInvoice(entityId, invoice.invoice_id);
        if (cancelled) return;
        setDetail(detailData);

        // If the detail response has a pdf_url, fetch the PDF through
        // the backend proxy. Skip for drafts (no PDF to fetch).
        if (detailData.pdf_url) {
          try {
            const pdfResp = await invoicingAPI.getInvoicePdf(entityId, invoice.invoice_id);
            if (cancelled) return;
            const blob = new Blob([pdfResp.data], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            setPdfUrl(url);
            setPhase('pdf');
          } catch (pdfErr) {
            // PDF fetch failed, fall back to line items
            if (cancelled) return;
            setPhase('detail');
          }
        } else {
          setPhase('detail');
        }
      } catch (err) {
        if (cancelled) return;
        setErrorMsg(getErrorMessage(err));
        setPhase('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [entityId, invoice]);

  // Clean up blob URL on unmount
  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  const label = invoice?.uplisting_id
    ? `Booking ${invoice.uplisting_id}`
    : (invoice?.invoice_number || invoice?.invoice_id || 'Invoice');

  return (
    <Dialog open={!!invoice} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className={phase === 'pdf' ? 'max-w-4xl h-[85vh] flex flex-col' : 'max-w-2xl'}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-blue-500" />
            {label}
          </DialogTitle>
          <DialogDescription>
            {phase === 'pdf' && 'PDF from Pennylane'}
            {phase === 'detail' && (
              detail?.draft
                ? (detail?.invoice_lines?.length > 0
                    ? 'PDF not available for draft invoices. Showing line items.'
                    : 'PDF not available for draft invoices.')
                : 'Invoice detail from Pennylane'
            )}
            {phase === 'loading' && 'Loading invoice...'}
            {phase === 'error' && 'Failed to load invoice'}
          </DialogDescription>
        </DialogHeader>

        {/* Loading */}
        {phase === 'loading' && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Loading invoice...</span>
          </div>
        )}

        {/* Error */}
        {phase === 'error' && (
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {errorMsg}
          </div>
        )}

        {/* PDF viewer */}
        {phase === 'pdf' && pdfUrl && (
          <iframe
            src={pdfUrl}
            className="w-full flex-1 rounded border"
            style={{ minHeight: '60vh' }}
            title={`Invoice ${label}`}
          />
        )}

        {/* Line-item fallback */}
        {phase === 'detail' && detail && (
          <div className="space-y-3 text-sm max-h-[65vh] overflow-y-auto">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <p className="text-xs text-muted-foreground">Invoice #</p>
                <p className="font-medium">
                  {detail.draft
                    ? <span className="text-muted-foreground italic">No legal invoice number until finalized</span>
                    : (detail.invoice_number || '-')}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Status</p>
                <p className="font-medium">{detail.draft ? 'Draft' : (detail.status || '-')}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Date</p>
                <p className="font-medium">{detail.date || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Currency</p>
                <p className="font-medium">{detail.currency || '-'}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <p className="text-xs text-muted-foreground">Amount</p>
                <p className="font-medium">
                  {detail.amount != null ? fmtAmount(detail.amount, detail.currency) : '-'}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Customer</p>
                <p className="font-medium">
                  {detail.customer?.name
                    || detail.customer?.first_name
                    || (typeof detail.customer === 'string' ? detail.customer : null)
                    || '-'}
                </p>
                {detail.customer_fetch_error && (
                  <p className="text-[10px] text-red-500 mt-0.5">{detail.customer_fetch_error}</p>
                )}
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Guest</p>
                <p className="font-medium">{detail.guest_name || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Uplisting ID</p>
                <p className="font-medium font-mono">{detail.uplisting_id || '-'}</p>
              </div>
            </div>

            {/* Lines */}
            {detail.invoice_lines?.length > 0 ? (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Invoice Lines</p>
                <div className="rounded border overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="text-left px-2 py-1 font-medium">Label</th>
                        <th className="text-right px-2 py-1 font-medium">Qty</th>
                        <th className="text-right px-2 py-1 font-medium">Unit Price</th>
                        <th className="text-right px-2 py-1 font-medium">Amount</th>
                        <th className="text-right px-2 py-1 font-medium">VAT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.invoice_lines.map((line, i) => (
                        <tr key={i} className="border-b">
                          <td className="px-2 py-1">{line.label || line.description || '-'}</td>
                          <td className="px-2 py-1 text-right">{line.quantity}</td>
                          <td className="px-2 py-1 text-right tabular-nums">
                            {(line.raw_currency_unit_price ?? line.unit_price) != null
                              ? fmtAmount(line.raw_currency_unit_price ?? line.unit_price, detail.currency)
                              : '-'}
                          </td>
                          <td className="px-2 py-1 text-right tabular-nums">
                            {fmtAmount(computeLineTotal(line), detail.currency)}
                          </td>
                          <td className="px-2 py-1 text-right">{line.vat_rate}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <ReconciliationRow
                  lines={detail.invoice_lines}
                  invoiceAmount={detail.amount}
                  currency={detail.currency}
                />
              </div>
            ) : (
              <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/50 p-3 text-xs text-amber-700 dark:text-amber-300">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <div>
                  <p className="font-medium">No line items returned by Pennylane for this {detail.draft ? 'draft' : 'invoice'}.</p>
                  {detail.lines_fetch_error && (
                    <p className="mt-0.5 text-red-600 dark:text-red-400">{detail.lines_fetch_error}</p>
                  )}
                  <p className="mt-0.5 text-amber-600 dark:text-amber-400">
                    Open the raw response below to inspect.
                  </p>
                </div>
              </div>
            )}

            {/* Raw Pennylane response (diagnostic) */}
            <div className="border rounded">
              <button
                type="button"
                onClick={() => setShowRaw((v) => !v)}
                className="w-full flex items-center justify-between px-3 py-2 text-xs text-muted-foreground hover:bg-muted/30 cursor-pointer"
              >
                <span>Raw Pennylane response ({detail.raw_response_keys?.length || 0} keys)</span>
                {showRaw
                  ? <ChevronUp className="h-3.5 w-3.5" />
                  : <ChevronDown className="h-3.5 w-3.5" />}
              </button>
              {showRaw && (
                <pre className="px-3 pb-3 text-[10px] leading-snug overflow-x-auto max-h-[40vh] overflow-y-auto whitespace-pre-wrap break-words font-mono bg-muted/20">
                  {JSON.stringify(detail.raw_pennylane_response, null, 2)}
                </pre>
              )}
            </div>

            {/* Finalize action for drafts */}
            {detail.draft && onRequestFinalize && (
              <div className="pt-2 border-t">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    onClose();
                    onRequestFinalize(invoice);
                  }}
                >
                  <Lock className="h-4 w-4 mr-1" />
                  Finalize this draft (irreversible)
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


// ── Finalize confirm dialog ──────────────────────────────────────────

function FinalizeConfirmDialog({ entityId, invoice, onClose, onFinalized }) {
  const [phase, setPhase] = useState('loading'); // loading | confirm | submitting | done | error | blocked
  const [detail, setDetail] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [blockedResp, setBlockedResp] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!invoice) return;
    let cancelled = false;

    (async () => {
      try {
        const { data } = await invoicingAPI.viewInvoice(entityId, invoice.invoice_id);
        if (cancelled) return;
        setDetail(data);
        setPhase('confirm');
      } catch (err) {
        if (cancelled) return;
        setErrorMsg(getErrorMessage(err));
        setPhase('error');
      }
    })();

    return () => { cancelled = true; };
  }, [entityId, invoice]);

  const handleFinalize = async () => {
    setPhase('submitting');
    try {
      const { data } = await invoicingAPI.finalizeInvoice(entityId, invoice.invoice_id);
      setResult(data);
      setPhase('done');
      onFinalized(data);
    } catch (err) {
      // FastAPI wraps HTTPException detail under .detail
      const resp = err?.response?.data?.detail ?? err?.response?.data;
      if (resp?.blocked) {
        setErrorMsg(resp.reason || 'Blocked by server guard.');
        setBlockedResp(resp);
        setPhase('blocked');
      } else {
        setErrorMsg(getErrorMessage(err));
        setPhase('error');
      }
    }
  };

  const lines = detail?.invoice_lines || [];
  const lineSum = lines.reduce((acc, l) => acc + computeLineTotal(l), 0);
  const total = Number(detail?.amount) || 0;
  const diff = Math.abs(lineSum - total);
  const mismatch = diff > 0.01;
  const hasCommission = lines.some(
    (l) => (l.label || '').toLowerCase().includes('commission') && Number(l.quantity) < 0
  );

  const label = invoice?.uplisting_id
    ? `Booking ${invoice.uplisting_id}`
    : (invoice?.invoice_number || invoice?.invoice_id || 'Invoice');

  return (
    <Dialog open={!!invoice} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-red-500" />
            Finalize {label}
          </DialogTitle>
          <DialogDescription>
            This action is irreversible. The draft will receive a legal invoice number and can no longer be edited or deleted.
          </DialogDescription>
        </DialogHeader>

        {phase === 'loading' && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Loading invoice detail...</span>
          </div>
        )}

        {phase === 'error' && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {errorMsg}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Close</Button>
            </DialogFooter>
          </div>
        )}

        {phase === 'blocked' && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <div>
                <p className="font-medium">Finalization blocked</p>
                <p className="mt-1">{errorMsg}</p>
              </div>
            </div>
            {blockedResp?.pennylane_error && (
              <div className="border rounded">
                <p className="px-3 py-2 text-xs text-muted-foreground font-medium">Pennylane response</p>
                <pre className="px-3 pb-3 text-[10px] leading-snug overflow-x-auto max-h-[30vh] overflow-y-auto whitespace-pre-wrap break-words font-mono bg-muted/20">
                  {typeof blockedResp.pennylane_error === 'string'
                    ? blockedResp.pennylane_error
                    : JSON.stringify(blockedResp.pennylane_error, null, 2)}
                </pre>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Close</Button>
            </DialogFooter>
          </div>
        )}

        {phase === 'confirm' && detail && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-muted-foreground">Customer</p>
                <p className="font-medium">
                  {detail.customer?.name || detail.customer?.first_name || '-'}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Guest</p>
                <p className="font-medium">{detail.guest_name || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Amount</p>
                <p className="font-medium">{fmtAmount(detail.amount, detail.currency)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Date</p>
                <p className="font-medium">{detail.date || '-'}</p>
              </div>
            </div>

            {/* Line items summary */}
            {lines.length > 0 ? (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Line items ({lines.length})</p>
                <div className="rounded border overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="text-left px-2 py-1 font-medium">Label</th>
                        <th className="text-right px-2 py-1 font-medium">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line, i) => (
                        <tr key={i} className="border-b">
                          <td className="px-2 py-1">{line.label || line.description || '-'}</td>
                          <td className="px-2 py-1 text-right tabular-nums">
                            {fmtAmount(computeLineTotal(line), detail.currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <ReconciliationRow lines={lines} invoiceAmount={detail.amount} currency={detail.currency} />
              </div>
            ) : (
              <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                No line items loaded. The server will re-verify before finalizing.
              </div>
            )}

            {/* Warnings */}
            {(mismatch || (!hasCommission && total > 0)) && lines.length > 0 && (
              <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                Reconciliation issues detected. The server will block finalization if these are not resolved.
              </div>
            )}

            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
              <p className="font-semibold">This cannot be undone.</p>
              <p className="mt-1">
                Once finalized, this invoice receives a legal number and is locked in Pennylane.
                It cannot be edited, re-drafted, or deleted.
              </p>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button
                variant="destructive"
                onClick={handleFinalize}
              >
                <Lock className="h-4 w-4 mr-1" />
                Finalize (irreversible)
              </Button>
            </DialogFooter>
          </div>
        )}

        {phase === 'submitting' && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Finalizing invoice...</span>
          </div>
        )}

        {phase === 'done' && result && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-700">
              <span className="font-medium">
                Invoice finalized. Legal number: {result.invoice_number || '(pending)'}
              </span>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Close</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


// ── Main list panel ──────────────────────────────────────────────────

export function InvoiceListPanel({ entityId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  // Cursor stack: each entry is the cursor used to fetch that page.
  // Index 0 = first page (cursor=null), index 1 = second page, etc.
  const [cursorStack, setCursorStack] = useState([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [finalizeInvoice, setFinalizeInvoice] = useState(null);

  const fetchInvoices = useCallback(async (cursor) => {
    setLoading(true);
    try {
      const { data: resp } = await invoicingAPI.listInvoices(entityId, cursor);
      setData(resp);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  useEffect(() => {
    setData(null);
    setCursorStack([null]);
    setPageIndex(0);
    setViewInvoice(null); // close modals on entity switch
    setFinalizeInvoice(null);
    fetchInvoices(null);
  }, [entityId, fetchInvoices]);

  const goNext = () => {
    if (!data?.next_cursor) return;
    const nextIdx = pageIndex + 1;
    setCursorStack((prev) => {
      const copy = [...prev];
      // Store the next_cursor at the next index
      if (copy.length <= nextIdx) copy.push(data.next_cursor);
      else copy[nextIdx] = data.next_cursor;
      return copy;
    });
    setPageIndex(nextIdx);
    fetchInvoices(data.next_cursor);
  };

  const goPrev = () => {
    if (pageIndex <= 0) return;
    const prevIdx = pageIndex - 1;
    setPageIndex(prevIdx);
    fetchInvoices(cursorStack[prevIdx]);
  };

  const invoices = data?.invoices || [];
  const hasMore = data?.has_more || false;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-500" />
            Pennylane Invoices
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Booking invoices from Pennylane for this entity (newest first).
          </p>
        </CardHeader>
        <CardContent>
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Loading invoices...</span>
            </div>
          )}

          {!loading && data?.message && invoices.length === 0 && (
            <div className="rounded-lg border p-6 text-center text-sm text-muted-foreground">
              {data.message}
            </div>
          )}

          {!loading && !data?.message && invoices.length === 0 && (
            <div className="rounded-lg border p-6 text-center">
              <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                No invoices yet. Generate from the Control Panel.
              </p>
            </div>
          )}

          {!loading && invoices.length > 0 && (
            <div className="space-y-3">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left px-3 py-2 font-medium">Ref</th>
                      <th className="text-left px-3 py-2 font-medium">Guest</th>
                      <th className="text-left px-3 py-2 font-medium">Customer</th>
                      <th className="text-left px-3 py-2 font-medium">Date</th>
                      <th className="text-right px-3 py-2 font-medium">Amount</th>
                      <th className="text-center px-3 py-2 font-medium">Status</th>
                      <th className="text-center px-3 py-2 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => (
                      <tr key={inv.invoice_id} className="border-b hover:bg-muted/20">
                        <td className="px-3 py-2">
                          <span className="font-mono text-xs" title={inv.invoice_number ? `Invoice #${inv.invoice_number}` : `Pennylane ID: ${inv.invoice_id}`}>
                            {inv.uplisting_id || inv.invoice_number || inv.invoice_id}
                          </span>
                          {inv.uplisting_id && inv.invoice_number && (
                            <span className="block text-[10px] text-muted-foreground">#{inv.invoice_number}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 max-w-[120px] truncate" title={inv.guest_name}>
                          {inv.guest_name || '-'}
                        </td>
                        <td className="px-3 py-2 max-w-[160px] truncate" title={inv.customer_name}>
                          {inv.customer_name || '-'}
                        </td>
                        <td className="px-3 py-2">{inv.date || '-'}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium">
                          {fmtAmount(inv.amount, inv.currency)}
                        </td>
                        <td className="px-3 py-2 text-center">{statusBadge(inv)}</td>
                        <td className="px-3 py-2 text-center">
                          <div className="inline-flex items-center gap-1.5">
                            <button
                              onClick={() => setViewInvoice(inv)}
                              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs cursor-pointer"
                              title="View invoice"
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </button>
                            {inv.draft && (
                              <button
                                onClick={() => setFinalizeInvoice(inv)}
                                className="inline-flex items-center gap-1 text-red-600 hover:text-red-800 text-xs cursor-pointer"
                                title="Finalize invoice (irreversible)"
                              >
                                <Lock className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {(pageIndex > 0 || hasMore) && (
                <div className="flex items-center justify-between pt-2">
                  <span className="text-xs text-muted-foreground">
                    Page {pageIndex + 1}{invoices.length > 0 ? ` (${invoices.length} shown)` : ''}
                  </span>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={loading || pageIndex <= 0}
                      onClick={goPrev}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={loading || !hasMore}
                      onClick={goNext}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {viewInvoice && (
        <InvoiceDetailModal
          entityId={entityId}
          invoice={viewInvoice}
          onClose={() => setViewInvoice(null)}
          onRequestFinalize={(inv) => setFinalizeInvoice(inv)}
        />
      )}

      {finalizeInvoice && (
        <FinalizeConfirmDialog
          entityId={entityId}
          invoice={finalizeInvoice}
          onClose={() => setFinalizeInvoice(null)}
          onFinalized={(result) => {
            // Refresh the current page to reflect the new status
            fetchInvoices(cursorStack[pageIndex]);
          }}
        />
      )}
    </>
  );
}
